import { ChatListItem } from '../../models/chat';
import { IconChevronL, IconChevronR, IconPlus } from '../Icons';
import RelativeTime from '../RelativeTime';

interface ChatRailProps {
  chats: ChatListItem[];
  currentChatId: string | null;
  isDraftMode: boolean;
  messagesByChat: Record<string, { content: string; createdAt: string }[]>;
  onSelect: (chatId: string) => void;
  onBeginDraft: () => void;
  collapsed: boolean;
  onToggle: () => void;
}

export default function ChatRail({
  chats,
  currentChatId,
  isDraftMode,
  messagesByChat,
  onSelect,
  onBeginDraft,
  collapsed,
  onToggle,
}: ChatRailProps) {
  if (collapsed) {
    return (
      <aside
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '16px 0',
          gap: 6,
          borderRight: '1px solid var(--line)',
          background: 'var(--bg)',
          minWidth: 0,
        }}
      >
        <button
          type="button"
          className="btn ghost sm icon"
          onClick={onToggle}
          title="Expand chats"
          style={{ width: 28, height: 28, padding: 0 }}
        >
          <IconChevronR size={14} />
        </button>
        <button
          type="button"
          className="btn ghost sm icon"
          onClick={onBeginDraft}
          title="New chat"
          style={{ width: 28, height: 28, padding: 0 }}
        >
          <IconPlus size={14} />
        </button>
      </aside>
    );
  }

  return (
    <aside
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--line)',
        background: 'var(--bg)',
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: '16px 16px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span className="eyebrow">Chats</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            className="btn ghost sm"
            style={{ height: 24, padding: '0 6px' }}
            onClick={onBeginDraft}
            title="New chat"
          >
            <IconPlus size={14} />
          </button>
          <button
            type="button"
            className="btn ghost sm icon"
            style={{ height: 24, width: 24, padding: 0 }}
            onClick={onToggle}
            title="Collapse chats"
          >
            <IconChevronL size={14} />
          </button>
        </div>
      </div>

      <div
        className="scroll-thin"
        style={{ flex: 1, overflow: 'auto', padding: '0 8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {isDraftMode && (
          <button
            type="button"
            onClick={onBeginDraft}
            className="side-link active"
            style={{ height: 'auto', padding: '10px 10px', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>New chat</div>
              <div style={{ fontSize: 11, color: 'var(--text-6)' }}>draft</div>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>Type a prompt below to start.</div>
          </button>
        )}
        {chats.map((c) => {
          const isActive = c.id === currentChatId && !isDraftMode;
          const lastMsg = messagesByChat[c.id]?.[messagesByChat[c.id].length - 1];
          const preview = lastMsg?.content?.trim() || c.name;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id)}
              className={'side-link' + (isActive ? ' active' : '')}
              style={{
                height: 'auto',
                padding: '10px 10px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                background: isActive ? undefined : 'transparent',
                border: 'none',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, width: '100%' }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: isActive ? 'var(--text)' : 'var(--text-2)',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.name}
                </div>
                <RelativeTime style={{ fontSize: 11, color: 'var(--text-6)', flex: 'none' }} value={c.updatedAt} />
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-5)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  fontWeight: 400,
                }}
              >
                {preview}
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
}
